import type { ComboboxItem } from '@mantine/core';
import { Divider, Group, MultiSelect, SegmentedControl, Select } from '@mantine/core';
import { MonthPickerInput } from '@mantine/dates';
import { useForm } from '@mantine/form';
import dayjs from 'dayjs';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { View as ViewMode } from '../../../../containers/add-edit-financials/add-edit-financials.type';
import { useWorkPeriodsData } from '../../../../containers/work-periods/hooks/work-periods.hooks';
import { getRecentlyCompletedMonth } from '../../../../helpers/general-helpers';
import { useFinancialsStore } from '../../../../store/financials-store/financials-store';
import {
  resetAllFinancialsFilters,
  setBoard,
  setEndDate,
  setStartDate,
  setTeam,
  setWorkPeriodIds,
} from '../../../../store/financials-store/financials-store.actions';
import type { State } from '../../../../store/financials-store/financials-store.type';
import { Charts as ChartsType, View } from '../../../../store/financials-store/financials-store.type';
import { useGlobalStore } from '../../../../store/global-store/global-store';
import { baseWhite, secondaryBase, skyDark } from '../../../../styles/design-tokens';
import { Button } from '../../../../ui-library/button/button';
import { Icon } from '../../../../ui-library/icon/icon';
import { usePortfolioProjectsAndBoards } from '../../financials.hooks';
import { getCompletedSprintOptions } from './header.helpers';
import { useDateSync } from './use-date-sync';

interface DateRangeForm {
  dateRange: [Date | null, Date | null];
  team: string | null;
  board: string | null;
  workPeriodIds: string[];
}

export function Header({ setView }: { setView: (view: View) => void }) {
  const { t } = useTranslation();
  const portfolio = useGlobalStore((state) => state.portfolio);
  const startDate = useFinancialsStore((state: State) => state.startDate);
  const endDate = useFinancialsStore((state: State) => state.endDate);
  const team = useFinancialsStore((state: State) => state.team);
  const board = useFinancialsStore((state: State) => state.board);
  const workPeriodIds = useFinancialsStore((state: State) => state.workPeriodIds);
  const selectedChart = useFinancialsStore((state: State) => state.chart);

  const [teamOptions, setTeamOptions] = useState<ComboboxItem[]>([]);
  const [boardOptions, setBoardOptions] = useState<ComboboxItem[]>([]);
  const [viewMode, setViewMode] = useState<ViewMode>(ViewMode.ByDate);

  const { data: projects } = usePortfolioProjectsAndBoards(portfolio?.id);

  const form = useForm<DateRangeForm>({
    initialValues: {
      dateRange: [
        typeof startDate === 'string' ? new Date(startDate) : startDate instanceof Date ? startDate : null,
        typeof endDate === 'string' ? new Date(endDate) : endDate instanceof Date ? endDate : null,
      ],
      team: team ?? null,
      board: board ?? null,
      workPeriodIds: workPeriodIds || [],
    },
  });

  // Fetch work periods based on the currently selected board in the form
  const { data: workPeriods } = useWorkPeriodsData(
    form.values.board && form.values.board !== '' ? form.values.board : null,
  );

  // THIS EFFECT HANDLES TEAM SELECTION
  // biome-ignore lint/correctness/useExhaustiveDependencies: form methods are stable (useCallback) but accessed via unstable `form` object — including form.x in deps causes re-renders with the React Compiler
  useEffect(() => {
    const allTeamsOption: ComboboxItem = { label: t('financials.placeholders.all_teams'), value: '' };
    const allBoardsOption: ComboboxItem = { label: t('financials.placeholders.all_boards'), value: '' };

    // scenario 1: no team or board selected -> show all team and board options
    if (!form.values.team && !form.values.board) {
      setTeamOptions([
        allTeamsOption,
        ...(projects?.map((project) => ({ label: project.name, value: project.id })) ?? []),
      ]);
      setBoardOptions([
        allBoardsOption,
        ...(projects?.flatMap((project) =>
          project.subprojects.map((subproject) => ({ label: subproject.name, value: subproject.id })),
        ) ?? []),
      ]);
    }
    // scenario 2: team has a value, but the board does not, so we only show the boards for that team
    else if (form.values.team && !form.values.board) {
      setTeamOptions([
        allTeamsOption,
        ...(projects?.map((project) => ({ label: project.name, value: project.id })) ?? []),
      ]);
      const selectedProject = projects?.find((project) => project.id === form.values.team);
      const validBoardOptions =
        selectedProject?.subprojects.map((subproject) => ({
          label: subproject.name,
          value: subproject.id,
        })) ?? [];
      setBoardOptions([allBoardsOption, ...validBoardOptions]);
    }
    // scenario 3: board has a value, but the team does not.
    // this means we've selected 'all teams' in the drop down
    // so we clear the board form value and show all team and board options
    else if (form.values.board && !form.values.team) {
      setTeamOptions([
        allTeamsOption,
        ...(projects?.map((project) => ({ label: project.name, value: project.id })) ?? []),
      ]);
      setBoardOptions([
        allBoardsOption,
        ...(projects?.flatMap((project) =>
          project.subprojects.map((subproject) => ({ label: subproject.name, value: subproject.id })),
        ) ?? []),
      ]);
      form.setFieldValue('board', null);
    }
    // scenario 4: both team and board have values,
    // so we check if the board is under the team and if not, we clear the board form value
    else if (form.values.team && form.values.board) {
      const selectedProject = projects?.find((project) => project.id === form.values.team);
      const validBoardOptions =
        selectedProject?.subprojects.map((subproject) => ({
          label: subproject.name,
          value: subproject.id,
        })) ?? [];
      if (!validBoardOptions.some((b) => b.value === form.values.board)) {
        form.setFieldValue('board', null);
      }
      setBoardOptions([allBoardsOption, ...validBoardOptions]);
      setTeamOptions([
        allTeamsOption,
        ...(projects?.map((project) => ({ label: project.name, value: project.id })) ?? []),
      ]);
    }
  }, [form.values.team, projects]);

  // THIS EFFECT HANDLES BOARD SELECTION
  // biome-ignore lint/correctness/useExhaustiveDependencies: form methods are stable (useCallback) but accessed via unstable `form` object — including form.x in deps causes re-renders with the React Compiler
  useEffect(() => {
    const allTeamsOption: ComboboxItem = { label: t('financials.placeholders.all_teams'), value: '' };
    const allBoardsOption: ComboboxItem = { label: t('financials.placeholders.all_boards'), value: '' };

    // scenario 1: no team or board selected -> show all team and board options
    if (!form.values.team && !form.values.board) {
      setTeamOptions([
        allTeamsOption,
        ...(projects?.map((project) => ({ label: project.name, value: project.id })) ?? []),
      ]);
      setBoardOptions([
        allBoardsOption,
        ...(projects?.flatMap((project) =>
          project.subprojects.map((subproject) => ({ label: subproject.name, value: subproject.id })),
        ) ?? []),
      ]);
    }
    // scenario 2: team has a value, but the board does not.
    // this means we've selected 'all boards' in the drop down (which may not be possible when a team is selected)
    // we only show the boards for that team
    else if (form.values.team && !form.values.board) {
      setTeamOptions([
        allTeamsOption,
        ...(projects?.map((project) => ({ label: project.name, value: project.id })) ?? []),
      ]);
      const selectedProject = projects?.find((project) => project.id === form.values.team);
      const validBoardOptions =
        selectedProject?.subprojects.map((subproject) => ({
          label: subproject.name,
          value: subproject.id,
        })) ?? [];
      setBoardOptions([allBoardsOption, ...validBoardOptions]);
    }
    // scenario 3: board has a value, but the team does not,
    // we should select the team that the board belongs to
    else if (form.values.board && !form.values.team) {
      setTeamOptions([
        allTeamsOption,
        ...(projects?.map((project) => ({ label: project.name, value: project.id })) ?? []),
      ]);
      setBoardOptions([
        allBoardsOption,
        ...(projects?.flatMap((project) =>
          project.subprojects.map((subproject) => ({ label: subproject.name, value: subproject.id })),
        ) ?? []),
      ]);
      const parentTeamId =
        projects?.find((project) => project.subprojects.find((subproject) => subproject.id === form.values.board))
          ?.id ?? null;
      if (form.values.team !== parentTeamId) {
        form.setFieldValue('team', parentTeamId);
      }
    }
    // scenario 4: both team and board have values,
    // so we check if the team is the parent of the board, and if not, we set the team form value to the board's parent project
    else if (form.values.team && form.values.board) {
      const selectedProject = projects?.find((project) => project.id === form.values.team);
      const validBoardOptions =
        selectedProject?.subprojects.map((subproject) => ({
          label: subproject.name,
          value: subproject.id,
        })) ?? [];
      if (!validBoardOptions.some((b) => b.value === form.values.board)) {
        const parentTeamId =
          projects?.find((project) => project.subprojects.find((subproject) => subproject.id === form.values.board))
            ?.id ?? null;
        if (form.values.team !== parentTeamId) {
          form.setFieldValue('team', parentTeamId);
        }
      }
      setBoardOptions([allBoardsOption, ...validBoardOptions]);
      setTeamOptions([
        allTeamsOption,
        ...(projects?.map((project) => ({ label: project.name, value: project.id })) ?? []),
      ]);
    }
  }, [form.values.board, projects]);

  // Sync store dates → form dateRange. Extracted to useDateSync hook for testability.
  useDateSync(form, startDate, endDate, selectedChart);

  // Update form work period IDs when they change in the store
  // biome-ignore lint/correctness/useExhaustiveDependencies: intentionally excluding form.values.workPeriodIds to avoid fighting with user input
  useEffect(() => {
    // Sync form with store when store changes
    const storeIds = workPeriodIds || [];

    if (storeIds.length > 0) {
      // If workPeriods data is available, validate the IDs
      if (workPeriods) {
        const validWorkPeriods = workPeriods.filter((wp) => storeIds.includes(wp.id));
        form.setFieldValue(
          'workPeriodIds',
          validWorkPeriods.map((wp) => wp.id),
        );
      } else {
        // WorkPeriods not loaded yet, just use store IDs
        form.setFieldValue('workPeriodIds', storeIds);
      }
    } else {
      // Store has no work period IDs, clear form
      form.setFieldValue('workPeriodIds', []);
    }

    form.resetDirty();
  }, [workPeriodIds, workPeriods]);

  // Clear work periods when board changes or is cleared
  // biome-ignore lint/correctness/useExhaustiveDependencies: intentional on board change
  useEffect(() => {
    setWorkPeriodIds([]);
    form.setFieldValue('workPeriodIds', []);
    if (!board || board === '') {
      setViewMode(ViewMode.ByDate);
    }
  }, [board]);

  // Handle view mode change
  const handleChangeView = (newViewMode: ViewMode) => {
    setViewMode(newViewMode);
    if (newViewMode === ViewMode.ByDate) {
      // Only clear the form value, don't update the store until Apply is clicked
      form.setFieldValue('workPeriodIds', []);
    }
  };

  const normalizeEndDate = (value: Date | null): Date | null => {
    if (!value) {
      return null;
    }
    const endDateDayjs = dayjs(value);
    const isCurrentMonth = endDateDayjs.isSame(dayjs(), 'month');
    if (isCurrentMonth && selectedChart !== ChartsType.AgentCosts) {
      return getRecentlyCompletedMonth();
    }
    return endDateDayjs.endOf('month').toDate();
  };

  const toDate = (value: Date | string | null | undefined): Date | null => {
    if (!value) {
      return null;
    }
    if (value instanceof Date) {
      return value;
    }
    return new Date(value);
  };

  const handleApply = () => {
    const [selectedStartDate, selectedEndDate] = form.values.dateRange;

    const normalizedSelectedEndDate = normalizeEndDate(selectedEndDate ?? null);
    const currentStartDate = toDate(startDate);
    const currentEndDate = toDate(endDate);

    const hasStartDateChanged =
      (selectedStartDate ? selectedStartDate.getTime() : null) !==
      (currentStartDate ? currentStartDate.getTime() : null);
    const hasEndDateChanged =
      (normalizedSelectedEndDate ? normalizedSelectedEndDate.getTime() : null) !==
      (currentEndDate ? currentEndDate.getTime() : null);

    const hasDateRangeChanged = hasStartDateChanged || hasEndDateChanged;

    // Only update dates if in "By Date" mode
    if (viewMode === ViewMode.ByDate) {
      if (hasDateRangeChanged) {
        setStartDate(selectedStartDate ?? null);
        setEndDate(normalizedSelectedEndDate);
        // reset filters only when the date range actually changes
        resetAllFinancialsFilters();
      }
      // Always clear work period IDs when switching to "By Date" mode
      if (workPeriodIds.length > 0) {
        setWorkPeriodIds([]);
        resetAllFinancialsFilters();
      }
    }

    const selectedTeam = form.values.team || null;
    const selectedBoard = form.values.board || null;

    if (team !== selectedTeam) {
      setTeam(selectedTeam);
    }

    if (board !== selectedBoard) {
      setBoard(selectedBoard);
    }

    // Handle work period IDs in "By Sprint" mode
    if (viewMode === ViewMode.BySprint) {
      const selectedWorkPeriodIds = form.values.workPeriodIds || [];
      // Create sorted copies to avoid mutating original arrays
      const hasWorkPeriodsChanged =
        JSON.stringify([...workPeriodIds].sort()) !== JSON.stringify([...selectedWorkPeriodIds].sort());

      if (hasWorkPeriodsChanged) {
        setWorkPeriodIds(selectedWorkPeriodIds);
        // reset filters when work periods change
        resetAllFinancialsFilters();
      }
    }

    form.resetDirty();
  };

  const isApplyDisabled = !form.isDirty() || (viewMode === ViewMode.BySprint && form.values.workPeriodIds.length === 0);

  // Check if sprint mode is available (board must be selected in the form)
  const isSprintModeAvailable = Boolean(form.values.board && form.values.board !== '');

  return (
    <Group justify="space-between">
      <Group>
        <Select
          allowDeselect={false}
          placeholder={t('financials.placeholders.select_team')}
          size="xs"
          clearable={Boolean(!form.values.board)}
          onClear={() => form.setFieldValue('team', null)}
          data={teamOptions}
          radius="xl"
          searchable
          value={form.values.team || ''}
          onChange={(value) => form.setFieldValue('team', value || null)}
          style={{ minWidth: '150px' }}
        />
        <Select
          allowDeselect={false}
          placeholder={t('financials.placeholders.select_board')}
          size="xs"
          clearable
          onClear={() => form.setFieldValue('board', null)}
          data={boardOptions}
          radius="xl"
          searchable
          value={form.values.board || ''}
          onChange={(value) => form.setFieldValue('board', value || null)}
          style={{ minWidth: '150px' }}
        />
        {isSprintModeAvailable && (
          <>
            <SegmentedControl
              data={Object.values(ViewMode)}
              value={viewMode}
              onChange={(value) => handleChangeView(value as ViewMode)}
              size="sm"
              radius="xl"
              color={secondaryBase}
              styles={{
                root: {
                  padding: 0,
                },
              }}
            />
            <Divider orientation="vertical" />
          </>
        )}
        {viewMode === ViewMode.ByDate || !isSprintModeAvailable ? (
          <MonthPickerInput
            type="range"
            allowSingleDateInRange
            radius="xl"
            size="xs"
            style={{ width: '300px', minWidth: '250px' }}
            leftSection={<Icon name="calendar_month" color={secondaryBase} size={16} />}
            leftSectionPointerEvents="none"
            placeholder={t('financials.placeholders.pick_date_range')}
            value={form.values.dateRange}
            onChange={(value) => {
              const startDate = value[0] ? dayjs(value[0]).toDate() : null;
              const endDate = value[1] ? dayjs(value[1]).toDate() : null;
              form.setFieldValue('dateRange', [startDate, endDate]);
            }}
            maxDate={
              selectedChart === ChartsType.AgentCosts
                ? dayjs().endOf('month').toDate()
                : dayjs().subtract(1, 'month').toDate()
            }
            clearable
          />
        ) : (
          <MultiSelect
            placeholder={form.values.workPeriodIds.length === 0 ? t('financials.placeholders.select_sprints') : ''}
            size="xs"
            radius="xl"
            data={getCompletedSprintOptions(workPeriods)}
            value={form.values.workPeriodIds}
            onChange={(value: string[]) => {
              form.setFieldValue('workPeriodIds', value);
            }}
            style={{ minWidth: '250px' }}
            maxDropdownHeight={240}
            searchable
            error={form.values.workPeriodIds.length >= 7 ? 'You can only select up to 7 sprints to compare' : undefined}
            maxValues={7}
            styles={{
              input: {
                cursor: 'pointer',
              },
            }}
          />
        )}
        <Button
          type="submit"
          onClick={handleApply}
          variant="secondary"
          radius="xl"
          size="xs"
          disabled={isApplyDisabled}
          style={{ border: isApplyDisabled ? `1px solid ${skyDark}` : 'none' }}
        >
          {t('common.actions.apply')}
        </Button>
      </Group>
      <Button
        variant="primary"
        onClick={() => setView(View.Table)}
        radius="xl"
        size="xs"
        leftSection={<Icon name="edit" color={baseWhite} size={16} />}
      >
        {t('financials.table.edit_financial_data')}
      </Button>
    </Group>
  );
}
